import { IBidResponse } from '../../typings/IPrebid'
import { AdPlayer, PlayerState, IPlayerConfig } from '../adPlayer/AdPlayer'
import { ImaPlayer } from '../adPlayer/ImaPlayer'
import { DEFAULT_REFRESH_INTERVAL } from '../constants'
import EventManager from '../eventManager/EventManager'
import { doOnce } from '../helpers/doOnce'
import Logger from '../helpers/Logger'
import { AuctionResults } from '../prebidFacade/AuctionResults'
import { Slot } from '../slots/Slot'
import { getOutstreamPlayerSize } from './hasOutstream'

/**
 * We don't want to refresh the slot before the video ad finishes
 * so we bump up the refreshTime a bit.
 * */

const OUTSTREAM_REFRESH_TIME = 31

export class OutstreamRenderer {
  target: HTMLElement
  player: AdPlayer
  container: HTMLElement
  bidResponse?: IBidResponse

  constructor(private readonly slot: Slot) {
    this.container = initOutstreamContainer(slot.wrapper)
    slot.refreshTime = OUTSTREAM_REFRESH_TIME
  }

  async loadAd(bidResponse: IBidResponse) {
    Logger.debug('Outstream bid won:', bidResponse)
    const playerConfig = getPlayerConfig(this.slot, this.container)
    this.player = await ImaPlayer.new(playerConfig)
    this.registerEventHandlers()
    this.bidResponse = bidResponse
    await this.player.initVideoAd(bidResponse)
  }

  private registerEventHandlers({ player } = this) {
    const { events } = player
    player.onEvent(events.adError, this.onAdErrorHandler())
    player.onEvent(events.started, this.onAdStart())
    player.onEvent(events.adsCompleted, this.onAdsComplete())
    player.onEvent(events.visibilityChange, this.onVisibilityChange())
    player.onEvent(events.impression, this.onImpression())
  }

  private onVisibilityChange = ({ player } = this) => (isVisible: boolean) => {
    if (player.playerState === PlayerState.init) {
      return
    }
    isVisible ? player.play() : player.pause()
  }

  private onImpression = ({ slot } = this) => () => {
    EventManager.trigger(EventManager.events.refreshSlot, slot)
  }

  /**
   * We only want to play the first second of an ad if it is off the screen
   * this.adPlayStatus will be 'resume' if the ad has been on screen
   */
  private onAdStart = doOnce(({ player } = this) => () => {
    setTimeout(() => {
      if (player.hasNotBeenOnScreen) {
        player.pause()
      }
    }, 1000)
  })

  private onAdErrorHandler = ({ bidResponse, player, slot } = this) =>
    /**
     * Less than ideal that we're coupled to ima here
     * RFF: Change NR Event to accept a generic error object so we can
     * construct our own error in the IMAPlayer.
     * */
    (adErrorEvent: google.ima.AdErrorEvent) => {
      const adError = adErrorEvent.getError()
      Logger.debug(adError)
      if (!bidResponse) {
        return
      }
      EventManager.trigger(EventManager.events.outstreamAdError, {
        adError,
        bidResponse
      })
      player.cleanup()
      // This will trigger a refresh without doing a new auction, relying on what is in the AuctionHouse
      const auctionResultSet = [new AuctionResults(slot.model, slot, {})]
      EventManager.trigger(EventManager.events.bidReady, auctionResultSet)
    }

  /** Refresh slot NOW if the ad has ended */
  private onAdsComplete = ({ slot, player } = this) => () => {
    slot.refreshTime = DEFAULT_REFRESH_INTERVAL
    EventManager.trigger(EventManager.events.shortCircuitRefresh, slot)
    player.cleanup()
  }
}

const initOutstreamContainer = (target: HTMLElement) => {
  // Create the ad display container.
  const outstreamContainer = document.createElement('div')
  outstreamContainer.className = 'mv-outstream-container'
  target.insertAdjacentElement('afterbegin', outstreamContainer)

  return outstreamContainer
}

const getPlayerConfig = (slot: Slot, container: HTMLElement): IPlayerConfig => {
  const [width, height] = getOutstreamPlayerSize(
    slot,
    slot.wrapper.parentElement
  )

  return {
    container,
    autoplay: true,
    muted: true,
    height,
    width
  }
}
